import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Inbox, FileText, Receipt, Truck, UserCircle, Activity, LayoutDashboard, Calendar, Users,
  MoreHorizontal, X, Building2, Package, Star, Settings, BarChart3, ClipboardCheck,
  ShieldAlert, HeartPulse, ShieldCheck, Shield, Lock, TrendingUp, Target, Clock, Wrench, Pill, Hospital, Send, Map as MapIcon,
  Radio, CheckCircle2, BadgeCheck, Award, DollarSign, CreditCard, Ambulance, AlertCircle
} from 'lucide-react';
import UpgradeModal from './UpgradeModal';
import { isSectionLocked as isSectionLockedForUser } from '../utils/modules';

// Module → section mapping (mirror exacto del Sidebar.jsx — debe mantenerse
// en sync. Los slugs son los reales del backend modules.slug, no nombres ES).

// Bottom nav: 3-4 items "primary" siempre visibles + sheet "Más" con el resto.
// Los labels e iconos coinciden 1:1 con el Sidebar.jsx para que el usuario
// que alterna entre escritorio y móvil reconozca cada sección.
const MOBILE_NAV = {
  client: {
    primary: [
      { id: 'dashboard', label: 'Resumen', icon: LayoutDashboard },
      { id: 'requests', label: 'Mis Solicitudes', icon: Inbox },
      { id: 'quotes', label: 'Presupuestos', icon: FileText },
      { id: 'invoices', label: 'Facturas', icon: Receipt },
    ],
    more: [],
  },
  professional: {
    primary: [
      { id: 'dashboard', label: 'Mi Panel', icon: LayoutDashboard },
      { id: 'services', label: 'Servicios', icon: Truck },
      { id: 'availability', label: 'Disponibilidad', icon: Calendar },
      { id: 'profile', label: 'Mi Perfil', icon: UserCircle },
    ],
    more: [
      { id: 'billing', label: 'Liquidaciones', icon: Receipt },
    ],
  },
  ambulance: {
    primary: [
      { id: 'dashboard', label: 'Panel', icon: LayoutDashboard },
      { id: 'dispatch', label: 'Centralita', icon: Send },
      { id: 'patients', label: 'Pacientes', icon: Users },
      { id: 'history', label: 'Historial', icon: FileText },
    ],
    more: [],
  },
  admin: {
    primary: [
      { id: 'dashboard', label: 'Panel', icon: LayoutDashboard },
      { id: 'dispatch', label: 'Centralita', icon: Radio },
      { id: 'requests', label: 'DRP', icon: AlertCircle },
    ],
    // Mismo orden y agrupación que el sidebar agrupado tras la redistribución:
    // vista general → tiempo real → red asistencial → comercial → personal →
    // flota → configuración. Iconos sin duplicidades por grupo.
    more: [
      { header: 'Vista general' },
      { id: 'executive', label: 'Vista ejecutiva', icon: TrendingUp },
      { id: 'reporting', label: 'Informes', icon: BarChart3 },
      { header: 'Operaciones tiempo real' },
      { id: 'dispatch_map', label: 'Mapa centralita', icon: MapIcon },
      { id: 'transport_requests', label: 'Servicios sanitarios', icon: Ambulance },
      { id: 'calendar', label: 'Calendario', icon: Calendar },
      { id: 'services', label: 'Servicios en curso', icon: Clock },
      { header: 'Red asistencial' },
      { id: 'patients', label: 'Partes clínicos', icon: FileText },
      { id: 'hospitals', label: 'Hospitales', icon: Hospital },
      { id: 'quality', label: 'Calidad / ISO', icon: CheckCircle2 },
      { header: 'Comercial' },
      { id: 'clients', label: 'Clientes', icon: Users },
      { id: 'leads', label: 'Leads / CRM', icon: Target },
      { id: 'rates', label: 'Tarifas', icon: DollarSign },
      { id: 'billing', label: 'Facturación', icon: CreditCard },
      { header: 'Personal' },
      { id: 'professionals', label: 'Profesionales', icon: BadgeCheck },
      { id: 'certifications', label: 'Certificaciones', icon: Award },
      { id: 'shifts', label: 'Turnos y fichaje', icon: Clock },
      { header: 'Flota y material' },
      { id: 'resources', label: 'Recursos', icon: Truck },
      { id: 'checklists', label: 'Checklists', icon: ClipboardCheck },
      { id: 'maintenance', label: 'Mantenimiento', icon: Wrench },
      { id: 'stock', label: 'Inventario', icon: Pill },
      { id: 'insurance', label: 'Pólizas seguros', icon: Shield },
      { header: 'Configuración' },
      { id: 'users', label: 'Usuarios', icon: UserCircle },
      { id: 'custom_roles', label: 'Roles personalizados', icon: ShieldCheck },
      { id: 'profile', label: 'Mi cuenta', icon: UserCircle },
      { id: 'settings', label: 'Ajustes', icon: Settings },
    ],
  },
  hospital: {
    primary: [
      { id: 'dashboard', label: 'Resumen', icon: LayoutDashboard },
      { id: 'service_requests', label: 'Solicitar', icon: Send },
      { id: 'patients', label: 'Pacientes', icon: HeartPulse },
      { id: 'profile', label: 'Mi cuenta', icon: UserCircle },
    ],
    more: [],
  },
  superadmin: {
    primary: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'tenants', label: 'Clientes SaaS', icon: Building2 },
      { id: 'tickets', label: 'Soporte', icon: Inbox },
    ],
    more: [
      { header: 'Vista general' },
      { id: 'tenantMetrics', label: 'Métricas Tenant', icon: BarChart3 },
      { header: 'Catálogo plataforma' },
      { id: 'plans', label: 'Planes', icon: Star },
      { id: 'modules', label: 'Módulos', icon: Package },
      { id: 'categories', label: 'Categorías profesionales', icon: Users },
      { id: 'vehicleTypes', label: 'Tipos vehículo', icon: Truck },
      { header: 'Sistema' },
      { id: 'health', label: 'Salud sistema', icon: HeartPulse },
      { id: 'audit', label: 'Audit log', icon: ShieldAlert },
      { id: 'settings', label: 'Ajustes', icon: Settings },
      { header: 'Cuenta' },
      { id: 'profile', label: 'Mi cuenta', icon: UserCircle },
    ],
  },
};

export default function MobileBottomNav({ currentSection, onSectionChange }) {
  const { user } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState(null);
  const role = user?.role;
  const config = MOBILE_NAV[role];
  const allowedSections = user?.allowed_sections;
  const customRoleActive = role === 'admin' && Array.isArray(allowedSections);

  if (!config) return null;

  // Locking: misma lógica que Sidebar (utils/modules.js).
  const isLocked = (sectionId) => role !== 'superadmin' && isSectionLockedForUser(user, sectionId);

  // Soft-RBAC custom role: si el admin tiene allowed_sections, filtramos
  // primary y more (incluso headers) a solo lo permitido. Headers se mantienen
  // si tienen al menos 1 item del rol; si no, se omiten para no dejar separadores huérfanos.
  const filterByCustomRole = (items) => {
    if (!customRoleActive) return items;
    const out = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.header) {
        // Solo añadimos el header si el SIGUIENTE bloque tiene algún allowed
        let hasAny = false;
        for (let j = i + 1; j < items.length && !items[j].header; j++) {
          if (allowedSections.includes(items[j].id)) { hasAny = true; break; }
        }
        if (hasAny) out.push(it);
      } else if (allowedSections.includes(it.id)) {
        out.push(it);
      }
    }
    return out;
  };
  const primary = filterByCustomRole(config.primary);
  const more = filterByCustomRole(config.more);
  // Filter out section headers when checking length / active for the "Más" button
  const moreSections = more.filter(m => !m.header);
  const hasMore = moreSections.length > 0;
  const moreActive = moreSections.some(m => m.id === currentSection && !isLocked(m.id));

  const handleClick = (item) => {
    if (isLocked(item.id)) {
      setUpgradeFeature(item.label);
      setMoreOpen(false);
      return;
    }
    onSectionChange(item.id);
    setMoreOpen(false);
  };

  return (
    <>
      <nav className="mobile-bottom-nav">
        {primary.map(item => {
          const Icon = item.icon;
          const locked = isLocked(item.id);
          const isActive = currentSection === item.id && !locked;
          return (
            <button
              key={item.id}
              className={isActive ? 'active' : ''}
              onClick={() => handleClick(item)}
              aria-disabled={locked || undefined}
              title={locked ? `${item.label} — No incluido en tu plan` : item.label}
              style={locked ? { opacity: 0.6, position: 'relative' } : { position: 'relative' }}
            >
              <Icon size={22} strokeWidth={isActive ? 2.5 : 1.5} />
              <span>{item.label}</span>
              {locked && (
                <Lock size={10} style={{ position: 'absolute', top: 6, right: 10, color: 'var(--text-muted)' }} aria-label="No incluido en tu plan" />
              )}
            </button>
          );
        })}
        {hasMore && (
          <button className={moreActive ? 'active' : ''} onClick={() => setMoreOpen(true)}>
            <MoreHorizontal size={22} strokeWidth={moreActive ? 2.5 : 1.5} />
            <span>Más</span>
          </button>
        )}
      </nav>

      {/* "Más" sheet */}
      {moreOpen && (
        <div className="mobile-more-overlay" onClick={() => setMoreOpen(false)}>
          <div className="mobile-more-sheet" onClick={e => e.stopPropagation()}>
            <div className="mobile-more-header">
              <span>Más opciones</span>
              <button onClick={() => setMoreOpen(false)} aria-label="Cerrar"><X size={20} /></button>
            </div>
            <div className="mobile-more-grid">
              {more.map((item, idx) => {
                // Headers ocupan toda la fila y no tienen onClick
                if (item.header) {
                  return (
                    <div key={`h-${idx}`} className="mobile-more-header-section" style={{
                      gridColumn: '1 / -1',
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.6px',
                      color: 'var(--text-dim)', textTransform: 'uppercase',
                      padding: '10px 4px 4px', userSelect: 'none',
                    }}>
                      {item.header}
                    </div>
                  );
                }
                const Icon = item.icon;
                const locked = isLocked(item.id);
                const isActive = currentSection === item.id && !locked;
                return (
                  <button
                    key={item.id}
                    className={`mobile-more-item ${isActive ? 'active' : ''}`}
                    onClick={() => handleClick(item)}
                    aria-disabled={locked || undefined}
                    title={locked ? `${item.label} — No incluido en tu plan` : item.label}
                    style={locked ? { opacity: 0.6, position: 'relative' } : { position: 'relative' }}
                  >
                    <Icon size={22} strokeWidth={isActive ? 2.5 : 1.5} />
                    <span>{item.label}</span>
                    {locked && (
                      <Lock size={12} style={{ position: 'absolute', top: 8, right: 8, color: 'var(--text-muted)' }} aria-label="No incluido en tu plan" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <UpgradeModal
        open={!!upgradeFeature}
        feature={upgradeFeature}
        onClose={() => setUpgradeFeature(null)}
        onGoToPlans={() => onSectionChange('settings')}
      />
    </>
  );
}
